import React from 'react';
import { FiZap, FiUsers, FiTool } from 'react-icons/fi';
import { useAgentMetrics } from '../../hooks/useAgentAnalytics';
import type { AnalyticsDateRange, AnalyticsPeriod } from '../../services/agentAnalyticsApi';

interface PerformanceCardProps {
  agentId: string;
  period: AnalyticsPeriod;
  /** Intervalo personalizado — substitui a janela do `period` */
  range?: AnalyticsDateRange;
}

function formatLatency(seconds: number): string {
  if (seconds < 1) return `${Math.round(seconds * 1000)}ms`;
  return `${seconds.toFixed(1)}s`;
}

export const PerformanceCard: React.FC<PerformanceCardProps> = ({
  agentId,
  period,
  range,
}) => {
  const { data: metrics, isLoading } = useAgentMetrics(agentId, period, range);

  return (
    <div
      className="rounded-lg border p-4"
      style={{
        backgroundColor: 'var(--theme-card-background)',
        borderColor: 'var(--theme-card-border)',
      }}
    >
      <div className="flex items-center justify-between mb-3">
        <h3
          className="text-sm font-semibold"
          style={{ color: 'var(--theme-card-text)' }}
        >
          Performance
        </h3>
        <FiZap
          className="h-5 w-5"
          style={{ color: 'var(--theme-primary)' }}
        />
      </div>

      {isLoading && (
        <div
          className="text-sm"
          style={{ color: 'var(--theme-muted-text)' }}
        >
          Carregando...
        </div>
      )}

      {!isLoading && metrics && (
        <>
          <div
            className="text-3xl font-bold mb-1"
            style={{ color: 'var(--theme-card-text)' }}
          >
            {metrics.totalExecutions}
          </div>
          <div
            className="text-xs mb-3"
            style={{ color: 'var(--theme-muted-text)' }}
          >
            execuções no período
          </div>

          <div className="space-y-1.5 text-sm">
            <div className="flex justify-between">
              <span style={{ color: 'var(--theme-muted-text)' }}>
                Latência média
              </span>
              <span
                className="font-medium"
                style={{ color: 'var(--theme-card-text)' }}
              >
                {formatLatency(metrics.avgLatencySeconds)}
              </span>
            </div>
            <div className="flex justify-between">
              <span style={{ color: 'var(--theme-muted-text)' }}>
                p95 latência
              </span>
              <span
                className="font-medium"
                style={{ color: 'var(--theme-card-text)' }}
              >
                {formatLatency(metrics.p95LatencySeconds)}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span
                className="flex items-center gap-1"
                style={{ color: 'var(--theme-muted-text)' }}
              >
                <FiUsers className="h-3 w-3" />
                Conversas distintas
              </span>
              <span
                className="font-medium"
                style={{ color: 'var(--theme-card-text)' }}
              >
                {metrics.distinctSessions}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span
                className="flex items-center gap-1"
                style={{ color: 'var(--theme-muted-text)' }}
              >
                <FiTool className="h-3 w-3" />
                Com tool calls
              </span>
              <span
                className="font-medium"
                style={{ color: 'var(--theme-card-text)' }}
              >
                {metrics.executionsWithTools}
              </span>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
